<template>
  <div class="agent-task-stats">
    <div class="columns is-marginless is-multiline is-mobile">
      <div
        v-for="(stat, index) in statistics"
        :key="`task-stat-${index}`"
        class="column is-6-mobile is-6-tablet is-6-desktop is-3-fullhd"
      >
        <div class="box stat has-background-light">
          <div>
            <p class="is-size-6-mobile is-size-5">{{ stat.label }}</p>
            <p class="is-size-7 has-text-grey">
              <a v-if="stat.action" class="has-text-grey" @click="stat.action">
                <u>{{ stat.hint }}</u>
              </a>
              <template v-else>{{ stat.hint }}</template>
            </p>
          </div>

          <span class="is-size-3-mobile is-size-4 title has-white-space-nowrap">
            {{ stat.value }}
          </span>
        </div>
      </div>

      <div class="column is-12">
        <p class="is-size-7 has-text-grey">
          Tasks, response and rating figures cover tasks created in the selected
          period that this agent is assigned to. First response is the time
          until the first reply from any agent on the task, and leaves out
          scheduled tasks and tasks raised by agents. Customers waiting covers
          this agent's open tasks right now.
        </p>
      </div>
    </div>
    <b-loading :is-full-page="false" :active.sync="isLoading" />
  </div>
</template>

<script>
import moment from "moment";
import {
  collection,
  getDocs,
  orderBy,
  query,
  where
} from "@firebase/firestore";
import { formatDuration, getFirstResponseTime, statuses } from "@src/data/task";

const periodFormats = { day: "YYYYMMDD", month: "YYYYMM", year: "YYYY" };

export default {
  name: "AgentTaskStats",
  props: {
    userId: {
      type: String,
      required: true
    },
    // One of the `availableDates` options from `AgentStatsChart`
    period: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      ref: collection(this.$firestore, "tasks"),
      isLoading: false,
      periodTasks: null,
      openTasks: null
    };
  },
  computed: {
    ratedTasks() {
      return this.$_.filter(this.periodTasks, task => Number(task.rating) > 0);
    },
    statistics() {
      const loading = "…",
        none = "–";
      const stats = {
        tasks: { label: "Tasks", hint: "Created in period", value: loading },
        response: { label: "Avg. first response", hint: "", value: loading },
        rating: { label: "Avg. rating", hint: "", value: loading },
        waiting: { label: "Customers waiting", hint: "Now", value: loading }
      };

      if (this.periodTasks) {
        const responseTimes = this.periodTasks
          .map(getFirstResponseTime)
          .filter(this.$_.isNumber);
        const ratings = this.ratedTasks.map(task => Number(task.rating));

        stats.tasks.value = this.periodTasks.length;
        if (this.periodTasks.length) stats.tasks.action = this.showPeriodTasks;
        stats.response.value = responseTimes.length
          ? formatDuration(this.$_.mean(responseTimes))
          : none;
        stats.response.hint = `Across ${responseTimes.length} task(s)`;
        stats.rating.value = ratings.length
          ? `${this.$_.round(this.$_.mean(ratings), 1)} / 5`
          : none;
        if (ratings.length) {
          stats.rating.hint = `View ${ratings.length} rating(s)`;
          stats.rating.action = this.showRatings;
        } else {
          stats.rating.hint = "No ratings yet";
        }
      }

      if (this.openTasks) {
        const waitingSince = this.openTasks
          .map(this.getWaitingSince)
          .filter(Boolean);

        stats.waiting.value = waitingSince.length;
        if (waitingSince.length)
          stats.waiting.hint = `Now, longest ${formatDuration(
            Date.now() - this.$_.min(waitingSince)
          )}`;
      }

      return this.$_.values(stats);
    }
  },
  watch: {
    "period.key": {
      handler: "getPeriodTasks",
      immediate: true
    }
  },
  created() {
    this.getOpenTasks();
  },
  methods: {
    getPeriodTasks() {
      const start = moment
        .utc(this.period.value, periodFormats[this.period.group])
        .startOf(this.period.group);
      const end = start.clone().add(1, this.period.group);
      const periodKey = this.period.key;

      this.isLoading = true;
      this.periodTasks = null;
      getDocs(
        query(
          this.ref,
          where("participants", "array-contains", this.userId),
          where("dateCreated", ">=", start.toDate()),
          where("dateCreated", "<", end.toDate())
        )
      )
        .then(snapshot => {
          // Ignore a slow response for a period that's since been changed
          if (periodKey !== this.period.key) return;
          this.periodTasks = snapshot.docs
            .map(doc => ({ ...doc.data(), _id: doc.id }))
            .filter(
              task =>
                task.status !== statuses.DELETED &&
                this.$_.includes(task.agents, this.userId)
            );
        })
        .catch(this.onError)
        .finally(() => {
          if (periodKey === this.period.key) this.isLoading = false;
        });
    },
    getOpenTasks() {
      getDocs(
        query(
          this.ref,
          where("agents", "array-contains", this.userId),
          where("status", ">", "1-"),
          orderBy("status", "desc"),
          orderBy("dateUpdated", "desc")
        )
      )
        .then(snapshot => {
          this.openTasks = snapshot.docs.map(doc => doc.data());
        })
        .catch(this.onError);
    },
    /* When the customer started waiting on an agent: either they've replied
    since an agent last interacted with the task, or it's a new task no agent
    has replied to yet. */
    getWaitingSince(task) {
      if (task.dateSinceAgentInteraction)
        return task.dateSinceAgentInteraction.toMillis();
      if (
        task.status === statuses.NEW &&
        !this.$_.get(task, "stats.dateOfAgentFirstReply")
      )
        return task.dateCreated.toMillis();
      return null;
    },
    showTasks({ title, tasks, showFeedback = false }) {
      this.$modal.open({
        component: () => import("./_agentTasksModal"),
        parent: this,
        width: 960,
        canCancel: ["escape", "outside"],
        hasModalCard: true,
        props: {
          title: `${title} · ${this.period.label}`,
          tasks,
          showFeedback
        }
      });
    },
    showPeriodTasks() {
      this.showTasks({
        title: "Tasks",
        tasks: this.$_.orderBy(
          this.periodTasks,
          task => task.dateCreated.toMillis(),
          "desc"
        )
      });
    },
    // Lowest ratings first (most in need of review), then newest
    showRatings() {
      this.showTasks({
        title: "Ratings",
        tasks: this.$_.orderBy(
          this.ratedTasks,
          [task => Number(task.rating), task => task.dateCreated.toMillis()],
          ["asc", "desc"]
        ),
        showFeedback: true
      });
    },
    onError() {
      this.$toast.open({
        message: "Error loading agent task stats",
        type: "is-danger"
      });
    }
  }
};
</script>

<style lang="scss" scoped>
@import "~@sass/bulma/custom-variables";

.agent-task-stats {
  position: relative;

  .stat {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: nowrap;

    @include mobile {
      flex-wrap: wrap;

      > * {
        width: 100%;
      }
    }
  }
}
</style>
